import { useEffect, useState } from "react"
import { Location, NavigateFunction, useLocation, useNavigate } from "react-router-dom"
import { ROUTES } from "@/routes/path"
import { AppRouteConfig } from "@/types/navigation"
import { getCurrentRouteContext } from "@/lib/utils/route"
import { useSelector } from "react-redux"
import { RootState } from "@/lib/store"
import { useListOrganizations } from "@/hooks/useOrganization"

let matched: AppRouteConfig

const checkJwtAuth = async (
  location: Location,
  navigate: NavigateFunction,
  authToken: string | null,
  organizations: any[]
) => {
  matched = getCurrentRouteContext(location)

  if (matched.path && matched.isError) {
    navigate(matched.path)
    return true
  }

  if (matched.isolated) {
    return false
  }

  // User is authenticated but trying to access a public view
  if (!matched.restricted && authToken) {
    if (organizations && organizations.length > 0) {
      navigate(ROUTES.DASHBOARD.STUDIO.INDEX)
      return true
    }
    navigate(ROUTES.DASHBOARD.ORGANIZATION.INDEX)
    return true
  }

  // User is authenticated but has no organizations
  if (matched.restricted && authToken) {
    if (organizations && organizations.length < 1 && matched.path && !matched.path.startsWith("/organization")) {
      navigate(ROUTES.DASHBOARD.ORGANIZATION.INDEX)
      return true
    }
  }

  // User is not authenticated but trying to access a restricted view
  if (matched.restricted && !authToken) {
    navigate(`${ROUTES.AUTH.LOGIN}?redirect=${encodeURIComponent(location.pathname)}`)
    return true
  }

  return false
}

interface InitializationProps {
  children: React.ReactNode
}

const Initialization = ({ children }: InitializationProps) => {
  const location = useLocation()
  const navigate: NavigateFunction = useNavigate()
  const [checking, setChecking] = useState(true)

  const { token } = useSelector((state: RootState) => state.auth)
  const { organizations } = useSelector((state: RootState) => state.organization)

  useListOrganizations(!!token)

  useEffect(() => {
    let isMounted = true
    checkJwtAuth(location, navigate, token, organizations).then((redirected) => {
      if (isMounted && !redirected) {
        setChecking(false)
      }
    })
    return () => {
      isMounted = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, navigate])

  if (checking) return <></>
  return <>{children}</>
}

export default Initialization
